/**
 * Utilitaires pour le tracking des performances cote client
 */

import { API_ENDPOINTS } from './apiConfig';

class PerformanceTracker {
  constructor() {
    this.metrics = [];
    this.MAX_METRICS = 100;

    // Initialiser le tracking des Web Vitals si disponible
    this.initWebVitals();

    // Tracker les erreurs non capturees
    this.initErrorTracking();
  }

  /**
   * Initialise le tracking des Core Web Vitals
   */
  initWebVitals() {
    if (typeof window === 'undefined') return;

    // Largest Contentful Paint (LCP)
    try {
      const lcpObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries[entries.length - 1];
        this.recordMetric('lcp', lastEntry.startTime);
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      // Non supporte
    }

    // First Input Delay (FID)
    try {
      const fidObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        entries.forEach(entry => {
          this.recordMetric('fid', entry.processingStart - entry.startTime);
        });
      });
      fidObserver.observe({ type: 'first-input', buffered: true });
    } catch (e) {
      // Non supporte
    }

    // Cumulative Layout Shift (CLS)
    try {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        }
        this.recordMetric('cls', clsValue);
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
    } catch (e) {
      // Non supporte
    }
  }

  /**
   * Initialise le tracking des erreurs globales
   */
  initErrorTracking() {
    if (typeof window === 'undefined') return;

    // Erreurs JavaScript non capturees
    window.addEventListener('error', (event) => {
      this.trackError({
        type: 'uncaught_error',
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack
      });
    });

    // Promesses rejetees non gerees
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError({
        type: 'unhandled_rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack
      });
    });

    // Intercepter console.error pour tracker toutes les erreurs loggées
    const originalConsoleError = console.error;
    console.error = (...args) => {
      // Éviter la récursion si trackError utilise console.error
      originalConsoleError.apply(console, args);

      // Construire le message
      const message = args.map(arg => {
        if (arg instanceof Error) {
          return `${arg.message}\n${arg.stack || ''}`;
        }
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      // Ne pas tracker les erreurs de réseau/fetch (trop de bruit)
      if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        return;
      }

      this.trackError({
        type: 'console_error',
        message: message.substring(0, 2000), // Limiter la taille
        source: 'console.error'
      });
    };

    // Intercepter console.warn pour les warnings importants
    const originalConsoleWarn = console.warn;
    console.warn = (...args) => {
      originalConsoleWarn.apply(console, args);

      const message = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      // Ne tracker que les warnings critiques (pas les deprecation warnings React)
      if (message.includes('Erreur') || message.includes('Error') ||
          message.includes('échec') || message.includes('failed') ||
          message.includes('invalid') || message.includes('invalide')) {
        this.trackError({
          type: 'console_warning',
          message: message.substring(0, 2000),
          source: 'console.warn',
          severity: 'warning'
        });
      }
    };
  }

  /**
   * Enregistre une metrique
   */
  recordMetric(name, value, metadata = {}) {
    const metric = {
      name,
      value,
      timestamp: Date.now(),
      url: window.location.pathname,
      ...metadata
    };

    this.metrics.push(metric);
    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics.shift();
    }

    // Log en developpement - désactivé pour réduire le bruit console
    // if (process.env.NODE_ENV === 'development') {
    //   console.log(`[Perf] ${name}: ${value}`, metadata);
    // }
  }

  /**
   * Mesure le temps d'une operation async
   */
  async measure(name, operation) {
    const start = performance.now();

    try {
      const result = await operation();
      const duration = Math.round(performance.now() - start);

      this.recordMetric(name, duration, { status: 'success' });

      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);

      this.recordMetric(name, duration, {
        status: 'error',
        error: error.message
      });

      throw error;
    }
  }

  /**
   * Tracker une requete API
   */
  trackApiCall(method, url, duration, statusCode) {
    this.recordMetric('api_call', duration, {
      method,
      url,
      statusCode,
      isError: statusCode >= 400,
      isSlow: duration > 1000
    });

    // Alerter si tres lent
    if (duration > 3000) {
      console.warn(`[Perf] Slow API call: ${method} ${url} took ${duration}ms`);
    }
  }

  /**
   * Tracker une erreur
   */
  async trackError(errorData) {
    const token = localStorage.getItem('token');

    this.recordMetric('error', 1, errorData);

    // Envoyer au serveur
    try {
      const baseUrl = API_ENDPOINTS.BASE_URL || 'http://localhost:3001';
      await fetch(`${baseUrl}/api/perf/client-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          error: errorData,
          url: window.location.href,
          userAgent: navigator.userAgent,
          metadata: {
            timestamp: new Date().toISOString(),
            metrics: this.getRecentMetrics(10)
          }
        })
      });
    } catch (e) {
      // Silencieux
    }
  }

  /**
   * Retourne les metriques recentes
   */
  getRecentMetrics(limit = 50) {
    return this.metrics.slice(-limit);
  }

  /**
   * Retourne un resume des metriques
   */
  getSummary() {
    const apiCalls = this.metrics.filter(m => m.name === 'api_call');
    const errors = this.metrics.filter(m => m.name === 'error');

    const avgApiTime = apiCalls.length > 0
      ? Math.round(apiCalls.reduce((sum, m) => sum + m.value, 0) / apiCalls.length)
      : 0;

    return {
      totalMetrics: this.metrics.length,
      apiCalls: apiCalls.length,
      avgApiTimeMs: avgApiTime,
      errors: errors.length,
      lcp: this.metrics.find(m => m.name === 'lcp')?.value,
      fid: this.metrics.find(m => m.name === 'fid')?.value,
      cls: this.metrics.find(m => m.name === 'cls')?.value
    };
  }
}

// Singleton
const performanceTracker = new PerformanceTracker();

/**
 * Wrapper pour fetch avec tracking automatique
 */
export const trackedFetch = async (url, options = {}) => {
  const start = performance.now();
  const method = options.method || 'GET';

  try {
    const response = await fetch(url, options);
    const duration = Math.round(performance.now() - start);

    performanceTracker.trackApiCall(method, url, duration, response.status);

    return response;
  } catch (error) {
    const duration = Math.round(performance.now() - start);

    performanceTracker.trackApiCall(method, url, duration, 0);
    performanceTracker.trackError({
      type: 'fetch_error',
      message: error.message,
      url,
      method
    });

    throw error;
  }
};

export default performanceTracker;
